import { useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import DatePicker, { type DateObject } from 'react-multi-date-picker'
import {
  faAngleLeft,
  faAngleRight,
  faCalendarAlt,
} from '@fortawesome/pro-solid-svg-icons'
import FocusTrap from 'focus-trap-react'
import { DateTime } from 'luxon'

import { Input } from 'Components/_theme'
import { TertiaryButton } from 'Components/_theme/TertiaryButton/TertiaryButton'

import { useEscapeKeyListenerEffect } from 'Hooks'

import { getDatePattern } from 'Utilities'

import type {
  MapDaysProps,
  SingleDatePickerProps,
} from './SingleDatePicker.types'

const weekDays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

const isLastDayOfMonth = (date: DateObject): boolean => {
  const dt = DateTime.local(date.year, date.month.number, date.day)

  return dt.day === dt.daysInMonth
}

export const SingleDatePicker = (props: SingleDatePickerProps) => {
  const {
    ariaDescribedBy,
    ariaLabel,
    e2eSelector,
    hasError,
    id,
    maxDate,
    minDate,
    onChange,
    onlyMonthEnd,
    placeholder,
    value,
  } = props

  const intl = useIntl()

  const [isOpen, setIsOpen] = useState(false)
  // react-multi-date-picker doesn't export proper ref types (matches MultidatePicker pattern)
  const datepickerRef = useRef<HTMLElement & { closeCalendar: () => void }>(
    null,
  )

  const handleChange = (selectedDate: DateObject | DateObject[] | null) => {
    if (!selectedDate || Array.isArray(selectedDate)) {
      onChange(null)

      return
    }

    onChange(selectedDate.toDate())
  }

  const handleCloseDatepicker = () => {
    setIsOpen(false)

    datepickerRef.current?.closeCalendar()
  }

  useEscapeKeyListenerEffect(handleCloseDatepicker, isOpen, datepickerRef)

  const mapDays = ({ date }: MapDaysProps) => {
    if (!onlyMonthEnd) {
      return {}
    }

    if (!isLastDayOfMonth(date)) {
      return {
        className: 'rmdp-disabled',
        disabled: true,
      }
    }

    return {}
  }

  const datePickerValue = value ? new Date(value) : undefined

  return (
    <FocusTrap
      active={isOpen}
      focusTrapOptions={{ clickOutsideDeactivates: true, preventScroll: true }}
    >
      <DatePicker
        ref={datepickerRef}
        arrow={false}
        className="MultidatePicker"
        containerClassName="MultidatePicker-container"
        format={getDatePattern()}
        mapDays={mapDays}
        maxDate={maxDate ? new Date(maxDate) : undefined}
        minDate={minDate ? new Date(minDate) : undefined}
        numberOfMonths={1}
        onChange={handleChange}
        onClose={() => setIsOpen(false)}
        onOpen={() => setIsOpen(true)}
        render={(stringValue: string, openCalendar: () => void) => (
          <Input
            aria-describedby={ariaDescribedBy}
            aria-label={ariaLabel}
            autoComplete="off"
            data-e2e={e2eSelector}
            iconLeft={faCalendarAlt}
            id={id}
            isInvalid={hasError}
            onClick={openCalendar}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                openCalendar()
              }
            }}
            placeholder={placeholder}
            readOnly
            value={stringValue}
          />
        )}
        renderButton={(
          direction: 'left' | 'right',
          handleClick: () => void,
        ) => (
          <TertiaryButton
            aria-label={
              direction === 'right'
                ? intl.formatMessage({ id: 'DatePicker_nextMonth' })
                : intl.formatMessage({ id: 'DatePicker_previousMonth' })
            }
            asIcon={direction === 'right' ? faAngleRight : faAngleLeft}
            onClick={handleClick}
            size="small"
          />
        )}
        value={datePickerValue}
        weekDays={weekDays}
      />
    </FocusTrap>
  )
}
